import React__default, { createElement, createRef, PureComponent } from 'react';
import classnames from 'classnames';
import debounce from 'lodash/debounce';
import posed, { PoseGroup } from 'react-pose';
import { Icon } from '@grandvision/icons';

function Article(_a) {
    var children = _a.children;
    return createElement("article", { className: "article" }, children);
}

/*! *****************************************************************************
Copyright (c) Microsoft Corporation. All rights reserved.
Licensed under the Apache License, Version 2.0 (the "License"); you may not use
this file except in compliance with the License. You may obtain a copy of the
License at http://www.apache.org/licenses/LICENSE-2.0
THIS CODE IS PROVIDED ON AN *AS IS* BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
KIND, EITHER EXPRESS OR IMPLIED, INCLUDING WITHOUT LIMITATION ANY IMPLIED
WARRANTIES OR CONDITIONS OF TITLE, FITNESS FOR A PARTICULAR PURPOSE,
MERCHANTABLITY OR NON-INFRINGEMENT.
See the Apache Version 2.0 License for specific language governing permissions
and limitations under the License.
***************************************************************************** */
var extendStatics = function(d, b) {
    extendStatics = Object.setPrototypeOf ||
        ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
        function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
    return extendStatics(d, b);
};
function __extends(d, b) {
    extendStatics(d, b);
    function __() { this.constructor = d; }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
}
var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

var PosedContainer = posed.div();
var PosedIcon = posed.span({
    buttonWithDropdownIdle: {
        rotate: 0,
    },
    buttonWithDropdownIsActive: {
        rotate: 180,
    },
});
var PosedContent = posed.div({
    enter: {
        opacity: 1,
        y: 0,
        applyAtStart: { overflow: 'hidden' },
        applyAtEnd: { overflow: '' },
    },
    exit: {
        opacity: 0,
        y: -25,
        applyAtStart: { overflow: 'hidden' },
        transition: { duration: 0 },
    },
});
var ButtonWithDropdown = /** @class */ (function (_super) {
    __extends(ButtonWithDropdown, _super);
    function ButtonWithDropdown() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.state = {
            offset: 0,
            isActive: typeof _this.props.isActive === 'boolean'
                ? _this.props.isActive
                : _this.props.initialActive || false,
        };
        _this.contentRef = React__default.createRef();
        _this.handleResize = debounce(function () { return _this.setOffset(); }, 60);
        _this.setOffset = function () {
            if (_this.contentRef.current && !_this.props.isFullWidth) {
                var innerWidth_1 = window.innerWidth;
                var right = _this.contentRef.current.getBoundingClientRect().right;
                var scrollBarWidth = 25; // IE needs this
                var offset = Math.min(innerWidth_1 - (right - _this.state.offset + scrollBarWidth), 0);
                if (offset !== _this.state.offset) {
                    _this.setState({ offset: offset });
                }
            }
        };
        _this.handleClick = function () {
            var isControlled = typeof _this.props.isActive === 'boolean';
            var isActive = !(isControlled ? _this.props.isActive : _this.state.isActive);
            !isControlled && _this.setState(function () { return ({ isActive: isActive }); });
            _this.props.onClick && _this.props.onClick(isActive);
        };
        return _this;
    }
    ButtonWithDropdown.prototype.componentDidMount = function () {
        window.addEventListener('resize', this.handleResize);
        this.setOffset();
    };
    ButtonWithDropdown.prototype.componentWillUnmount = function () {
        window.removeEventListener('resize', this.handleResize);
    };
    ButtonWithDropdown.prototype.componentDidUpdate = function (prevProps, prevState) {
        if (prevProps.isActive !== this.props.isActive || prevState.isActive !== this.state.isActive) {
            this.setOffset();
        }
    };
    ButtonWithDropdown.prototype.render = function () {
        var _a = this.props, label = _a.label, children = _a.children, isFullWidth = _a.isFullWidth, title = _a.title, isDisabled = _a.isDisabled, className = _a.className, rest = __rest(_a, ["label", "children", "isFullWidth", "title", "isDisabled", "className"]);
        var offset = this.state.offset;
        var isControlled = typeof this.props.isActive === 'boolean';
        var isActive = isControlled ? this.props.isActive : this.state.isActive;
        var dropdownClassnames = classnames('button-with-dropdown', { _is_full_width: isFullWidth });
        var buttonClassnames = classnames('button-with-dropdown__button', className, {
            _is_active: isActive,
            _is_full_width: !!isFullWidth,
        });
        var contentClassnames = classnames('button-with-dropdown__content', {
            _is_active: isActive,
            _is_full_width: !!isFullWidth,
            _is_disabled: isDisabled,
        });
        return (React__default.createElement(PosedContainer, { className: dropdownClassnames, pose: isActive ? 'buttonWithDropdownIsActive' : 'buttonWithDropdownIdle', withParent: false },
            React__default.createElement("button", { className: buttonClassnames, onClick: this.handleClick, type: "button", "aria-haspopup": "true", "aria-expanded": isActive, "aria-label": rest['aria-label'] || title, "aria-labelledby": rest['aria-labelledby'], "aria-describedby": rest['aria-describedby'], "aria-invalid": rest['aria-invalid'] },
                React__default.createElement("span", { className: "button-with-dropdown__button-inner" },
                    React__default.createElement("span", { className: "button-with-dropdown__label" }, label),
                    !isDisabled && (React__default.createElement(PosedIcon, { className: "button-with-dropdown__icon" },
                        React__default.createElement(Icon, { name: "chevron-down" }))))),
            React__default.createElement("div", { className: "button-with-dropdown__content-container", style: { left: offset } },
                React__default.createElement(PoseGroup, { flipMove: false }, isActive && (React__default.createElement(PosedContent, { key: "_", className: contentClassnames, ref: this.contentRef }, children))))));
    };
    return ButtonWithDropdown;
}(React__default.Component));

var Checkbox = /** @class */ (function (_super) {
    __extends(Checkbox, _super);
    function Checkbox() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.inputRef = createRef();
        _this.onClick = function (e) {
            _this.props.onChange({
                value: _this.props.value,
                checked: e.currentTarget.checked,
            });
        };
        return _this;
    }
    Checkbox.prototype.render = function () {
        var _a = this.props, id = _a.id, checked = _a.checked, disabled = _a.disabled, rest = __rest(_a, ["id", "checked", "disabled"]);
        return (createElement("label", { className: classnames('checkbox', this.props.className) },
            createElement("input", { className: "checkbox__input", type: "checkbox", id: id, "aria-checked": checked, "aria-label": rest['aria-label'], checked: typeof checked === 'boolean' ? checked : false, disabled: disabled, onChange: disabled ? undefined : this.onClick, "data-t": this.props['data-t'], tabIndex: 0, ref: this.inputRef, value: "0" // Not really used anywhere but it's a required attribute
             }),
            createElement("div", { className: "checkbox__mask" }),
            checked === 'mixed' ? (createElement("div", { className: "checkbox__indeterminate" })) : (createElement("svg", { className: "checkbox__checkmark", width: "14", height: "13", viewBox: "0 0 14 13", fill: "none", xmlns: "http://www.w3.org/2000/svg" },
                createElement("path", { d: "M1 7.5L4.5 12L13 1", fill: "none" })))));
    };
    Checkbox.prototype.componentDidUpdate = function (prevProps) {
        if (this.inputRef.current) {
            this.inputRef.current.indeterminate = this.props.checked === 'mixed';
        }
    };
    Checkbox.defaultProps = {
        checked: false,
        disabled: false,
    };
    return Checkbox;
}(PureComponent));

var Container = function (_a) {
    var children = _a.children, className = _a.className;
    return (createElement("div", { className: classnames('container', className) }, children));
};

var Hr = function (_a) {
    var className = _a.className;
    return (createElement("hr", { className: classnames('horizontal-rule', className) }));
};

var Label = function (_a) {
    var type = _a.type, className = _a.className, children = _a.children;
    return (createElement("b", { className: classnames('label', "label--" + type, className) }, children));
};

var Tag = function (_a) {
    var children = _a.children, onClick = _a.onClick;
    return (createElement("button", { className: "tag", onClick: onClick, type: "button" },
        children,
        createElement(Icon, { className: "tag__clear", name: "clear" })));
};

var Display = function (_a) {
    var _b;
    var _c = _a.align, align = _c === void 0 ? 'inherit' : _c, _d = _a.color, color = _d === void 0 ? 'inherit' : _d, className = _a.className, _e = _a.component, component = _e === void 0 ? 'h1' : _e, _f = _a["data-t"], testId = _f === void 0 ? 'typography-display' : _f, rest = __rest(_a, ["align", "color", "className", "component", 'data-t']);
    var Component = component || 'h1';
    return (createElement(Component, __assign({ className: classnames('typography-display', className, (_b = {},
            _b["typography-display--align-" + align] = align !== 'inherit',
            _b["typography-display--color-" + color] = color !== 'inherit',
            _b)), "data-t": testId }, rest)));
};

var variantMap = {
    large: 'h1',
    medium: 'h2',
    small: 'h3',
    smallest: 'h4',
    subheading1: 'h5',
    subheading2: 'h6',
};
var Heading = function (_a) {
    var _b;
    var _c = _a.align, align = _c === void 0 ? 'inherit' : _c, className = _a.className, _d = _a.color, color = _d === void 0 ? 'inherit' : _d, component = _a.component, _e = _a.variant, variant = _e === void 0 ? 'large' : _e, _f = _a["data-t"], testId = _f === void 0 ? 'typography-heading' : _f, rest = __rest(_a, ["align", "className", "color", "component", "variant", 'data-t']);
    var Component = component || variantMap[variant];
    return (createElement(Component, __assign({ className: classnames('typography-heading', "typography-heading--" + variant, className, (_b = {},
            _b["typography-heading--align-" + align] = align !== 'inherit',
            _b["typography-heading--color-" + color] = color !== 'inherit',
            _b)), "data-t": testId }, rest)));
};

var variantMap$1 = {
    body: 'p',
    'body-head': 'strong',
    caption: 'small',
    'caption-head': 'strong',
    link: 'span',
    list: 'span',
};
var Text = function (_a) {
    var _b;
    var _c = _a.align, align = _c === void 0 ? 'inherit' : _c, className = _a.className, _d = _a.color, color = _d === void 0 ? 'inherit' : _d, component = _a.component, _e = _a.variant, variant = _e === void 0 ? 'body' : _e, _f = _a["data-t"], testId = _f === void 0 ? 'typography-text' : _f, rest = __rest(_a, ["align", "className", "color", "component", "variant", 'data-t']);
    var Component = component || variantMap$1[variant];
    return (createElement(Component, __assign({ className: classnames('typography-text', "typography-text--" + variant, className, (_b = {},
            _b["typography-text--align-" + align] = align !== 'inherit',
            _b["typography-text--color-" + color] = color !== 'inherit',
            _b)), "data-t": testId }, rest)));
};

var TextArea = function (_a) {
    var className = _a.className, disabled = _a.disabled, maxLength = _a.maxLength, onChange = _a.onChange, onBlur = _a.onBlur, placeholder = _a.placeholder, name = _a.name, id = _a.id, rest = __rest(_a, ["className", "disabled", "maxLength", "onChange", "onBlur", "placeholder", "name", "id"]);
    var resolvedId = id || name;
    var handleChange = React__default.useCallback(function (e) {
        !disabled && onChange && onChange(e.currentTarget.value);
    }, [disabled, onChange]);
    var handleBlur = React__default.useCallback(function (e) {
        !disabled && onBlur && onBlur(e.currentTarget.value);
    }, [disabled, onBlur]);
    return (React__default.createElement("textarea", __assign({}, rest, { name: name, id: resolvedId, className: classnames('textarea', className), disabled: disabled, maxLength: maxLength, onChange: handleChange, onBlur: handleBlur, placeholder: placeholder })));
};

var ResponsiveImage = function (_a) {
    var src = _a.src, srcSet = _a.srcSet, alt = _a.alt, className = _a.className, sizes = _a.sizes, dataT = _a["data-t"];
    return (createElement("img", { "data-t": dataT || 'responsive-image', width: "100%", className: classnames('responsive-image', className), src: src, srcSet: srcSet, sizes: sizes, alt: alt }));
};

export { Article, ButtonWithDropdown, Checkbox, Container, Display, Heading, Hr, Label, ResponsiveImage, Tag, Text, TextArea };
//# sourceMappingURL=index.es.js.map
